import React, { useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PropertyCard = ({ property }) => {
  const [showModal, setShowModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [fullPropertyData, setFullPropertyData] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);

  const getStatusColor = (status) => {
    switch(status) {
      case 'reformando': return 'bg-yellow-500 text-white shadow-md';
      case 'vendiendo': return 'bg-emerald-500 text-white shadow-md';
      case 'reservado': return 'bg-red-500 text-white shadow-md';
      case 'vendido': return 'bg-slate-800 text-white shadow-md';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'reformando': return 'En Reforma';
      case 'vendiendo': return 'En Venta';
      case 'reservado': return 'Reservado';
      case 'vendido': return 'Vendido';
      default: return status;
    }
  };

  // Mostrar primera imagen disponible o imagen por defecto
  const getMainImage = () => {
    if (property.images && property.images.length > 0) {
      return property.images[0].url;
    }
    return 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&h=600&fit=crop';
  };

  const openModal = async () => {
    setShowModal(true);
    setCurrentImageIndex(0);
    
    // Load full property data with all images
    if (!fullPropertyData) {
      setLoadingDetails(true);
      try {
        const response = await axios.get(`${API}/properties/${property.id}`);
        setFullPropertyData(response.data);
      } catch (error) {
        console.error('Error loading property details:', error);
      } finally {
        setLoadingDetails(false);
      }
    }
  };
  
  const closeModal = () => {
    setShowModal(false);
    setCurrentImageIndex(0);
  };
  
  // Use full data if available, otherwise use the listing data
  const displayProperty = fullPropertyData || property;

  const nextImage = () => {
    if (displayProperty.images && displayProperty.images.length > 1) {
      setCurrentImageIndex((prev) => 
        prev === displayProperty.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (displayProperty.images && displayProperty.images.length > 1) {
      setCurrentImageIndex((prev) => 
        prev === 0 ? displayProperty.images.length - 1 : prev - 1
      );
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer" onClick={openModal}>
        <div className="relative">
          <img 
            src={getMainImage()} 
            alt={property.title}
            className="w-full h-48 object-cover"
          />
          <div className={`absolute top-4 left-4 px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(property.status)}`}>
            {getStatusText(property.status)}
          </div>
          {/* Mostrar contador de imágenes */}
          {(property.total_images > 1 || (property.images && property.images.length > 1)) && (
            <div className="absolute top-4 right-4 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-sm">
              📷 {property.total_images || property.images.length}
            </div>
          )}
          {/* Mostrar indicador de videos */}
          {property.videos && property.videos.length > 0 && (
            <div className="absolute bottom-4 right-4 bg-red-600 bg-opacity-90 text-white px-2 py-1 rounded text-sm">
              🎥 {property.videos.length}
            </div>
          )}
        </div>
        <div className="p-6">
          <h3 className="text-xl font-bold mb-2 text-gray-800">{property.title}</h3>
          <p className="text-gray-600 mb-3">{property.description}</p>
          <div className="flex items-center text-sm text-gray-500 mb-3">
            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
            </svg>
            {property.neighborhood}, {property.address}
          </div>
          <div className="flex justify-between items-center text-sm text-gray-600">
            <span>{property.bedrooms} hab • {property.bathrooms} baños</span>
            <span>{property.size_m2} m²</span>
          </div>
        </div>
      </div>

      {/* Modal mejorado */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-lg w-full max-w-5xl max-h-full overflow-y-auto overflow-x-hidden mx-2 sm:mx-4">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl sm:text-2xl font-bold pr-4">{displayProperty.title}</h2>
                <button 
                  onClick={closeModal}
                  className="text-gray-500 hover:text-gray-700 text-2xl flex-shrink-0"
                >
                  ×
                </button>
              </div>
              
              {/* Loading indicator */}
              {loadingDetails && (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                  <span className="ml-3 text-gray-600">Cargando imágenes...</span>
                </div>
              )}
              
              {/* Galería de imágenes mejorada */}
              {displayProperty.images && displayProperty.images.length > 0 && !loadingDetails && (
                <div className="mb-6">
                  <h3 className="text-base sm:text-lg font-semibold mb-3">Galería de Imágenes ({displayProperty.images.length}) <span className="text-xs sm:text-sm font-normal text-gray-500">- Click para ampliar</span></h3>
                  <div className="relative">
                    <div className="aspect-[4/3] sm:aspect-[16/10] w-full">
                      <img 
                        src={displayProperty.images[currentImageIndex]?.url} 
                        alt={displayProperty.images[currentImageIndex]?.caption || displayProperty.title}
                        className="w-full h-full object-contain sm:object-cover rounded-lg cursor-zoom-in hover:opacity-90 transition-opacity bg-gray-100"
                        onClick={() => setShowLightbox(true)}
                      />
                    </div>
                    {displayProperty.images.length > 1 && (
                      <>
                        <button 
                          onClick={prevImage}
                          className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75"
                        >
                          <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <button 
                          onClick={nextImage}
                          className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75"
                        >
                          <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </>
                    )}
                    
                    {/* Botón de ampliar */}
                    <button 
                      onClick={() => setShowLightbox(true)}
                      className="absolute bottom-4 right-2 sm:right-4 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75"
                      title="Ampliar imagen"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                      </svg>
                    </button>
                    
                    {/* Indicador de imagen actual */}
                    {displayProperty.images.length > 1 && (
                      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
                        {currentImageIndex + 1} / {displayProperty.images.length}
                      </div>
                    )}
                  </div>
                  
                  {/* Miniaturas */}
                  {displayProperty.images.length > 1 && (
                    <div className="flex space-x-2 mt-4 overflow-x-auto">
                      {displayProperty.images.map((image, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentImageIndex(index)}
                          className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${
                            currentImageIndex === index ? 'border-purple-600' : 'border-gray-200'
                          }`}
                        >
                          <img 
                            src={image.url} 
                            alt={image.caption || `Imagen ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* Descripción de la imagen actual */}
                  {displayProperty.images[currentImageIndex]?.caption && (
                    <p className="text-sm text-gray-600 mt-2 italic">
                      {displayProperty.images[currentImageIndex].caption}
                    </p>
                  )}
                </div>
              )}

              {/* Lightbox para ver imagen a pantalla completa */}
              {showLightbox && displayProperty.images && (
                <div 
                  className="fixed inset-0 bg-black bg-opacity-95 z-[60] flex items-center justify-center"
                  onClick={() => setShowLightbox(false)}
                >
                  <button 
                    onClick={() => setShowLightbox(false)}
                    className="absolute top-4 right-4 text-white text-4xl hover:text-gray-300 z-10"
                  >
                    ×
                  </button>
                  
                  <img 
                    src={displayProperty.images[currentImageIndex]?.url} 
                    alt={displayProperty.images[currentImageIndex]?.caption || displayProperty.title}
                    className="max-w-full max-h-full object-contain p-4"
                    onClick={(e) => e.stopPropagation()}
                  />
                  
                  {displayProperty.images.length > 1 && (
                    <>
                      <button 
                        onClick={(e) => { e.stopPropagation(); prevImage(); }}
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 text-white p-3 rounded-full hover:bg-opacity-40"
                      >
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <button 
                        onClick={(e) => { e.stopPropagation(); nextImage(); }}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 text-white p-3 rounded-full hover:bg-opacity-40"
                      >
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-lg">
                        {currentImageIndex + 1} / {displayProperty.images.length}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Videos de YouTube */}
              {displayProperty.videos && displayProperty.videos.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3">Videos</h3>
                  <div className={`grid gap-4 ${displayProperty.videos.length === 1 ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
                    {displayProperty.videos.map((video, index) => {
                      // Extraer ID del video de YouTube (incluyendo YouTube Live)
                      const getYouTubeId = (url) => {
                        if (!url) return null;
                        
                        const patterns = [
                          /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([^#&?]*)/,
                          /youtube\.com\/live\/([^#&?]*)/,  // Para YouTube Live
                          /youtube\.com\/shorts\/([^#&?]*)/  // Para YouTube Shorts
                        ];
                        
                        for (const pattern of patterns) {
                          const match = url.match(pattern);
                          if (match && match[1] && match[1].length >= 11) {
                            return match[1].substring(0, 11); // YouTube IDs son de 11 caracteres
                          }
                        }
                        
                        return null;
                      };
                      
                      const videoId = getYouTubeId(video.youtube_url);
                      
                      return (
                        <div key={index} className={`${displayProperty.videos.length === 1 ? 'w-full' : 'aspect-video'}`}>
                          {videoId ? (
                            <div className={`${displayProperty.videos.length === 1 ? 'w-full' : 'aspect-video'}`} style={displayProperty.videos.length === 1 ? { paddingBottom: '56.25%', position: 'relative' } : {}}>
                              <iframe
                                src={`https://www.youtube.com/embed/${videoId}`}
                                title={video.title || `Video ${index + 1}`}
                                className={`rounded-lg ${displayProperty.videos.length === 1 ? 'absolute inset-0 w-full h-full' : 'w-full h-full'}`}
                                allowFullScreen
                              />
                            </div>
                          ) : (
                            <div className="w-full h-full bg-gray-200 rounded-lg flex items-center justify-center aspect-video">
                              <p className="text-gray-500">Video no disponible</p>
                            </div>
                          )}
                          {video.title && (
                            <p className="text-sm font-medium text-gray-900 mt-2">{video.title}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Detalles de la propiedad */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div className="min-w-0">
                  <h3 className="text-lg font-semibold mb-3">Descripción</h3>
                  <p className="text-gray-600 mb-4 break-words">{displayProperty.description}</p>
                  
                  <h3 className="text-lg font-semibold mb-3">Ubicación</h3>
                  <p className="text-gray-600 break-words">{displayProperty.address}</p>
                  <p className="text-gray-600">{displayProperty.neighborhood}, Barcelona</p>
                </div>
                
                <div className="min-w-0">
                  <h3 className="text-lg font-semibold mb-3">Características</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Habitaciones:</span>
                      <span className="font-semibold">{displayProperty.bedrooms}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Baños:</span>
                      <span className="font-semibold">{displayProperty.bathrooms}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Superficie:</span>
                      <span className="font-semibold">{displayProperty.size_m2} m²</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Tipo:</span>
                      <span className="font-semibold capitalize">{displayProperty.property_type}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Estado:</span>
                      <span className={`font-semibold px-2 py-1 rounded text-sm ${getStatusColor(displayProperty.status)}`}>
                        {getStatusText(displayProperty.status)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Botón de contacto */}
                  <div className="mt-6">
                    <button 
                      onClick={() => {
                        closeModal();
                        document.getElementById('contacto').scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="w-full bg-gradient-to-r from-purple-600 to-yellow-400 text-white py-3 px-6 rounded-lg font-semibold hover:from-purple-700 hover:to-yellow-500 transition-all duration-300"
                    >
                      Me Interesa Esta Propiedad
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const Properties = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/properties`);
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProperties = properties.filter(property => {
    if (filter === 'all') return true;
    return property.status === filter;
  });

  return (
    <section id="propiedades" className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4">Nuestras Propiedades</h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto px-4">
            Viviendas reformadas a estrenar, llave en mano, para entrar a vivir inmediatamente
          </p>
        </div>

        <div className="flex justify-center mb-8 px-4">
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
            <button 
              onClick={() => setFilter('all')}
              className={`px-4 sm:px-6 py-2 rounded-lg font-semibold transition-colors text-sm sm:text-base ${
                filter === 'all' 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-purple-100'
              }`}
            >
              Todas
            </button>
            <button 
              onClick={() => setFilter('reformando')}
              className={`px-4 sm:px-6 py-2 rounded-lg font-semibold transition-colors text-sm sm:text-base ${
                filter === 'reformando' 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-purple-100'
              }`}
            >
              En Reforma
            </button>
            <button 
              onClick={() => setFilter('vendiendo')}
              className={`px-4 sm:px-6 py-2 rounded-lg font-semibold transition-colors text-sm sm:text-base ${
                filter === 'vendiendo' 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-purple-100'
              }`}
            >
              En Venta
            </button>
            <button 
              onClick={() => setFilter('reservado')}
              className={`px-4 sm:px-6 py-2 rounded-lg font-semibold transition-colors text-sm sm:text-base ${
                filter === 'reservado' 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-purple-100'
              }`}
            >
              Reservadas
            </button>
            <button 
              onClick={() => setFilter('vendido')}
              className={`px-4 sm:px-6 py-2 rounded-lg font-semibold transition-colors text-sm sm:text-base ${
                filter === 'vendido' 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-purple-100'
              }`}
            >
              Vendidas
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-600 mx-auto"></div>
          </div>
        ) : filteredProperties.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-xl">No hay propiedades disponibles en este momento.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProperties.map((property) => (
              <PropertyCard key={property.id} property={property} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Properties;